import {
  GET_AI_WEB_SEARCH_RECOMMENDATION,
  GET_AI_WEB_SEARCH_TERMS,
  MLClient
} from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import { Button, TextStyle } from '@superops/ui-design-system';
import { isEmpty, update } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { EventEmitter, getTranslatedText } from '@nile/components';
import AIWebSearchResults from './AIWebSearchResults';
import AIContainerContext from '../../context/AIContainerContext';
import { useAIRecommendedSolutionsStore } from '@nile/components/src/Components/AI/store/aiRecommendedSolutionsStore.tsx';

const RecommendedWebSearchCard = ({
  ticketId,
  setWebSearchLoading,
  setShowWebSearch,
  onWebSearchDislike,
  setInitialLoad,
  aiRecommendedSolutions
}) => {
  const intl = useIntl();

  const emptyRecommendedTicketHeader = [
    getTranslatedText(
      intl,
      'msp.ai.recommended.tickets.empty.header.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.recommended.tickets.empty.header.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.recommended.tickets.empty.header.message.3'
    ),
    getTranslatedText(intl, 'msp.ai.recommended.tickets.empty.header.message.4')
  ];

  const searchTerms = useAIRecommendedSolutionsStore((s) => s.searchTerms);
  const setSearchTerms = useAIRecommendedSolutionsStore(
    (s) => s.setSearchTerms
  );

  const webSearchResult = useAIRecommendedSolutionsStore(
    (s) => s.webSearchResult
  );
  const setWebSearchResult = useAIRecommendedSolutionsStore(
    (s) => s.setWebSearchResult
  );

  const setSendWebSearchReplyResponse = useAIRecommendedSolutionsStore(
    (s) => s.setSendWebSearchReplyResponse
  );
  const setSendWebSearchReplyData = useAIRecommendedSolutionsStore(
    (s) => s.setSendWebSearchReplyData
  );

  const setShowSuggestedReply = useAIRecommendedSolutionsStore(
    (s) => s.setShowSuggestedReply
  );

  const aiContainerContext = useContext(AIContainerContext);

  const webSearchHeaderText = [
    getTranslatedText(
      intl,
      'msp.ai.recommended.solution.web.search.header.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.recommended.solution.web.search.header.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.recommended.solution.web.search.header.message.3'
    ),
    getTranslatedText(
      intl,
      'msp.ai.recommended.solution.web.search.header.message.4'
    )
  ];

  const updateHeaderText = (updatedText) => {
    if (aiContainerContext?.setCustomHeaderText && updatedText) {
      aiContainerContext.setCustomHeaderText(updatedText);
    }
  };

  const [getAIWebSearchRecommendation] = useLazyQuery(
    GET_AI_WEB_SEARCH_RECOMMENDATION,
    {
      client: MLClient,
      fetchPolicy: 'no-cache',
      onCompleted: (res) => {
        if (res) {
          setWebSearchLoading(false);
          setShowWebSearch(true);
          setWebSearchResult(res);
          updateHeaderText(webSearchHeaderText);
        }
      }
    }
  );

  const { loading: searchTermsLoading, data: aiSearchTerms } = useQuery(
    GET_AI_WEB_SEARCH_TERMS,
    {
      fetchPolicy: 'no-cache',
      client: MLClient,
      variables: {
        ticketId: ticketId
      },
      onCompleted: (res) => {
        if (res?.getAIWebSearchTerms) {
          setSearchTerms(res?.getAIWebSearchTerms);
          setInitialLoad(false);
          setWebSearchLoading(false);
          if (
            isEmpty(res?.getAIWebSearchTerms) &&
            isEmpty(aiRecommendedSolutions)
          ) {
            updateHeaderText(emptyRecommendedTicketHeader);
          }
        }
      }
    }
  );

  const onWebSearch = async (params) => {
    const updatedSearchTerms = searchTerms.map((term) => {
      if (term?.searchTermId === params?.searchTermId) {
        return {
          ...term,
          selected: true
        };
      }
      return term;
    });
    setSearchTerms(updatedSearchTerms);
    setWebSearchLoading(true);
    if (params?.searchTerm && params?.searchTermId) {
      const webSearchVariables = {
        searchTerm: params?.searchTerm,
        searchTermId: params?.searchTermId
      };

      const response = await getAIWebSearchRecommendation({
        variables: webSearchVariables
      });
    }
  };

  const onWebSearchClose = () => {
    setWebSearchResult(null);
    setSendWebSearchReplyResponse(null);
    setSendWebSearchReplyData(null);
    setShowSuggestedReply(false);
  };

  useEffect(() => {
    EventEmitter.subscribe('closeWebSearch', onWebSearchClose);
    return function cleanup() {
      EventEmitter.unsubscribe('closeWebSearch');
    };
  }, []);

  return (
    <>
      {!isEmpty(webSearchResult) && (
        <AIWebSearchResults
          data={webSearchResult}
          onWebSearchDislike={onWebSearchDislike}
          ticketId={ticketId}
        />
      )}

      {!isEmpty(searchTerms) && isEmpty(webSearchResult) && (
        <div className="p10 br10 ai-dark-bg flex col">
          <div className="fsize12 pb-5 search-term-border">
            <TextStyle variation="subdued" weight="Medium">
              <FormattedMessage id="common.search.web" />
            </TextStyle>
          </div>
          {searchTerms.map((term, idx) => {
            const isLastItem = searchTerms.length === idx + 1;
            const isSelected = term?.selected;
            const classNames = [
              'flex fx-sb g10 actr pt-5 pb-5',
              !isLastItem && 'search-term-border'
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div className={classNames}>
                <div className="flex">
                  <TextStyle variation={isSelected && 'subdued'}>
                    {term?.searchTerm}
                  </TextStyle>
                </div>
                <div className="flex">
                  <Button
                    aiOutline
                    size="tiny"
                    onClick={() => onWebSearch(term)}
                  >
                    <SVGIcon name="ArrowRight" size="sm" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default RecommendedWebSearchCard;
